import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useMemo, useState } from 'react';
import { usePaginator } from '../../../hooks/usePaginator';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import fuzzy from 'fuzzy';
import { SelectableTeamRow } from '../SelectableTeamRow';
import { useInstallPermissions } from '../../../contexts/InstallPermissionsContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { TeamSearchEmptyState } from '../../TeamSearchEmptyState';
import { ITEMS_PER_PAGE, MAX_VISIBLE_PAGE_BUTTONS } from '../../../constants/paginator';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function flattenTeams(teams, expandedIds, depth = 0) {
  return teams.flatMap(team => {
    const row = {
      team,
      depth
    };
    const shouldExpand = !expandedIds || expandedIds.has(team.id);
    if (shouldExpand && team.childTeams.length > 0) {
      return [row, ...flattenTeams(team.childTeams, expandedIds, depth + 1)];
    }
    return [row];
  });
}
function getInitialExpandedIds(teams) {
  const ids = new Set();
  function collect(ts) {
    ts.forEach(t => {
      if (t.childTeams.length > 0) {
        ids.add(t.id);
        collect(t.childTeams);
      }
    });
  }
  collect(teams);
  return ids;
}
export function SelectableTeamList({
  teams
}) {
  const {
    updatedTeams,
    setUpdatedTeams,
    initialTeamIds
  } = useInstallPermissions();
  const [expandedTeamIds, setExpandedTeamIds] = useState(() => getInitialExpandedIds(teams));
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchQuery = useDebounce(searchInput, 250);
  const allTeams = useMemo(() => flattenTeams(teams), [teams]);
  const searchMatchedTeams = useMemo(() => {
    if (!debouncedSearchQuery) {
      return null;
    }
    const fuzzyResults = fuzzy.filter(debouncedSearchQuery, allTeams, {
      extract: item => item.team.name
    });
    const seenIds = new Set();
    const result = [];
    for (const {
      original
    } of fuzzyResults) {
      if (seenIds.has(original.team.id)) {
        continue;
      }
      for (const item of flattenTeams([original.team])) {
        seenIds.add(item.team.id);
      }
      result.push(original.team);
    }
    return result;
  }, [allTeams, debouncedSearchQuery]);
  const topLevelTeams = searchMatchedTeams !== null && searchMatchedTeams !== void 0 ? searchMatchedTeams : teams;
  const {
    currentPage,
    pageCount,
    paginatedItems: paginatedTopLevel,
    setPage
  } = usePaginator(topLevelTeams, {
    itemsPerPage: ITEMS_PER_PAGE
  });
  const paginatedTeams = useMemo(() => flattenTeams(paginatedTopLevel, expandedTeamIds), [paginatedTopLevel, expandedTeamIds]);
  const handleChange = (teamId, checked) => {
    const initiallySelected = initialTeamIds.has(teamId);
    setUpdatedTeams(prev => {
      if (checked === initiallySelected) {
        const rest = _objectWithoutPropertiesLoose(prev, [teamId].map(_toPropertyKey));
        return rest;
      }
      return Object.assign({}, prev, {
        [teamId]: checked
      });
    });
  };
  const handleSearchChange = event => {
    setSearchInput(event.target.value);
    setPage(1);
  };
  const handleToggleExpand = teamId => {
    setExpandedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };
  const selectedCount = allTeams.filter(({
    team
  }) => {
    var _updatedTeams$team$id;
    return (_updatedTeams$team$id = updatedTeams[team.id]) !== null && _updatedTeams$team$id !== void 0 ? _updatedTeams$team$id : initialTeamIds.has(team.id);
  }).length;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "400",
      marginBlockEnd: "400",
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsxs(Text, {
        as: "h5",
        marginBlockEnd: "0",
        whiteSpace: "nowrap",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.installPermissions.options.specificTeams.teamsSelected",
          options: {
            selectedCount,
            count: allTeams.length
          }
        }), allTeams.length > 0 && /*#__PURE__*/_jsx(Text, {
          as: "span",
          color: "text-core-subtle",
          marginInlineStart: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.installPermissions.options.specificTeams.teamsSelectedCount",
            options: {
              selectedCount,
              count: allTeams.length
            }
          })
        })]
      }), /*#__PURE__*/_jsx(SearchInput, {
        maxInlineSize: "400px",
        marginInlineStart: "auto",
        value: searchInput,
        onChange: handleSearchChange,
        placeholder: I18n.text('app-governance-lib.installPermissions.options.specificTeams.search.placeholder')
      })]
    }), debouncedSearchQuery && topLevelTeams.length === 0 ? /*#__PURE__*/_jsx(TeamSearchEmptyState, {}) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-surface-default",
        borderColor: "border-core-subtle",
        borderWidth: "100",
        borderStyle: "solid",
        borderRadius: "transitional-container",
        inlineSize: "100%",
        children: paginatedTeams.map(({
          team,
          depth
        }, index) => {
          var _updatedTeams$team$id2;
          return /*#__PURE__*/_jsx(SelectableTeamRow, {
            isFirst: index === 0,
            teamName: team.name,
            depth: depth,
            checked: (_updatedTeams$team$id2 = updatedTeams[team.id]) !== null && _updatedTeams$team$id2 !== void 0 ? _updatedTeams$team$id2 : initialTeamIds.has(team.id),
            onChange: checked => handleChange(team.id, checked),
            hasChildren: team.childTeams.length > 0,
            isExpanded: expandedTeamIds.has(team.id),
            onToggleExpand: () => handleToggleExpand(team.id)
          }, team.id);
        })
      }), topLevelTeams.length > ITEMS_PER_PAGE && /*#__PURE__*/_jsx(Paginator, {
        page: currentPage,
        pageCount: pageCount,
        onPageChange: event => setPage(event.target.value),
        marginBlockStart: "400",
        maxVisiblePageButtons: MAX_VISIBLE_PAGE_BUTTONS
      })]
    })]
  });
}